// The in-memory API's two-step sign-in (HARDEN-mfa), as Better Auth's twoFactor plugin answers
// under `/v1/auth/two-factor/*` (services/api/src/auth/better-auth.ts):
// - enrolment: `enable` (password confirmed) makes an unverified row — a 32-character secret and
//   ten single-use recovery codes — and returns the `otpauth://` link; `verify-totp` with the
//   session turns it on. `disable` and `generate-backup-codes` take the password too;
// - sign-in: a password sign-in of an enrolled account on an untrusted device opens a 10-minute
//   challenge instead of a session; a TOTP or recovery code answers it. Five wrong codes end the
//   challenge (400 TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE), ten in a row lock the account's codes for
//   15 minutes (429 ACCOUNT_TEMPORARILY_LOCKED), an expired or missing one is 401
//   INVALID_TWO_FACTOR_COOKIE;
// - "Trust this device": the real plugin sets a 30-day trust-device cookie, so the next password
//   sign-in in that browser skips the code. The fake runs inside one browser, so trust is kept per
//   account. The seeded operator is enrolled with a fixed demo secret and trusted, as if that
//   cookie were already set: every existing test and spec signs in with "demo" and no code.
// Codes are real TOTP codes (`@better-auth/utils/otp`, the library Better Auth uses), so a test
// or an authenticator app computes them from the secret.
import { createOTP } from "@better-auth/utils/otp";

export type AuthFailure = { status: number; code: string };
type Row = { secret: string; backupCodes: string[]; enabled: boolean };

/** The seeded operator's authenticator secret (raw, as `createOTP` takes it; 32 characters). */
export const DEMO_TOTP_SECRET = "CompanyOSDemoOperatorTotpSecret1";
/** The seeded operator's recovery codes. */
export const DEMO_RECOVERY_CODES = [
  "demo1-00001",
  "demo2-00002",
  "demo3-00003",
  "demo4-00004",
  "demo5-00005",
  "demo6-00006",
  "demo7-00007",
  "demo8-00008",
  "demo9-00009",
  "demoa-0000a",
];

const ISSUER = "CompanyOS";
const CHALLENGE_MS = 10 * 60_000;
const CHALLENGE_TRIES = 5;
const LOCK_AFTER = 10;
const LOCK_MS = 15 * 60_000;
const ALNUM = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const LOWER = "abcdefghijklmnopqrstuvwxyz0123456789";

function random(n: number, alphabet: string): string {
  const bytes = crypto.getRandomValues(new Uint8Array(n));
  let out = "";
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return out;
}

const recoveryCodes = () =>
  Array.from({ length: 10 }, () => `${random(5, LOWER)}-${random(5, LOWER)}`);

/** The current 6-digit code of a raw secret (what an authenticator app shows now). */
export const totpOf = (secret: string): Promise<string> =>
  createOTP(secret, { digits: 6, period: 30 }).totp();

export type MfaDeps = {
  now: () => Date;
  /** `none`: an email-link account without a password. */
  password: (email: string, password: string) => "ok" | "wrong" | "none";
  /** The challenge was answered: sign the account in (a new session). */
  signedIn: (email: string) => void;
};

export function createMfaFake(
  d: MfaDeps,
  seed: { email: string; secret: string; backupCodes: string[]; trusted: boolean }[],
) {
  const rows = new Map<string, Row>();
  const trusted = new Set<string>();
  const failures = new Map<string, { count: number; lockedUntil: number }>();
  let challenge: { email: string; attempts: number; expiresAt: number } | null = null;
  for (const s of seed) {
    rows.set(s.email, { secret: s.secret, backupCodes: [...s.backupCodes], enabled: true });
    if (s.trusted) trusted.add(s.email);
  }

  const fail = (status: number, code: string): AuthFailure => ({ status, code });
  function passwordFailure(email: string, password: string): AuthFailure | null {
    const p = d.password(email, password);
    if (p === "none") return fail(400, "CREDENTIAL_ACCOUNT_NOT_FOUND");
    return p === "wrong" ? fail(400, "INVALID_PASSWORD") : null;
  }

  return {
    enrolled: (email: string) => rows.get(email)?.enabled === true,
    /** Test hook: the account's raw secret (enrolled or mid-enrolment). */
    secretOf: (email: string) => rows.get(email)?.secret ?? null,
    /** Test hook: the recovery codes not spent yet. */
    recoveryCodesOf: (email: string) => [...(rows.get(email)?.backupCodes ?? [])],
    /** Test hook: this browser trusts (or forgets) the account (the trust-device cookie). */
    setTrusted(email: string, on: boolean) {
      if (on) trusted.add(email);
      else trusted.delete(email);
    },
    /** After a right password: sign in, or open the challenge (no session yet). */
    afterPassword(email: string): "signed_in" | "two_factor" {
      challenge = null;
      if (!rows.get(email)?.enabled || trusted.has(email)) return "signed_in";
      challenge = { email, attempts: 0, expiresAt: d.now().getTime() + CHALLENGE_MS };
      return "two_factor";
    },
    clearChallenge() {
      challenge = null;
    },
    /**
     * WF-CONTROL's step-up (`verifyTOTP` with the session): a current code of an enrolled account,
     * checked as the sign-in challenge checks one; no state changes.
     */
    async stepUp(email: string, code: string): Promise<boolean> {
      const row = rows.get(email);
      if (!row?.enabled) return false;
      return createOTP(row.secret, { digits: 6, period: 30 }).verify(code.trim());
    },
    /** `verify-totp` / `verify-backup-code` on the sign-in challenge; null when signed in. */
    async verify(
      kind: "totp" | "backup",
      code: string,
      trustDevice: boolean,
    ): Promise<AuthFailure | null> {
      const t = d.now().getTime();
      const c = challenge;
      if (!c || c.expiresAt <= t) {
        challenge = null;
        return fail(401, "INVALID_TWO_FACTOR_COOKIE");
      }
      const lock = failures.get(c.email);
      if (lock && lock.lockedUntil > t) return fail(429, "ACCOUNT_TEMPORARILY_LOCKED");
      const row = rows.get(c.email);
      if (!row?.enabled) {
        challenge = null;
        return fail(400, "TWO_FACTOR_NOT_ENABLED");
      }
      const typed = code.trim();
      let ok = false;
      if (kind === "totp")
        ok = await createOTP(row.secret, { digits: 6, period: 30 }).verify(typed);
      else {
        const i = row.backupCodes.indexOf(typed.toLowerCase());
        if (i >= 0) {
          row.backupCodes.splice(i, 1); // single use
          ok = true;
        }
      }
      if (!ok) {
        c.attempts += 1;
        const f = failures.get(c.email) ?? { count: 0, lockedUntil: 0 };
        f.count += 1;
        failures.set(c.email, f);
        if (f.count >= LOCK_AFTER) {
          f.count = 0;
          f.lockedUntil = t + LOCK_MS;
          challenge = null;
          return fail(429, "ACCOUNT_TEMPORARILY_LOCKED");
        }
        if (c.attempts >= CHALLENGE_TRIES) {
          challenge = null;
          return fail(400, "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE");
        }
        return fail(401, kind === "totp" ? "INVALID_CODE" : "INVALID_BACKUP_CODE");
      }
      failures.delete(c.email);
      challenge = null;
      if (trustDevice) trusted.add(c.email);
      d.signedIn(c.email);
      return null;
    },
    /** `enable`: an unverified row (replacing an earlier unverified one). */
    enable(
      email: string,
      password: string,
    ): { totpURI: string; backupCodes: string[] } | AuthFailure {
      const bad = passwordFailure(email, password);
      if (bad) return bad;
      if (rows.get(email)?.enabled) return fail(400, "TOTP_ALREADY_ENABLED");
      const secret = random(32, ALNUM);
      const backupCodes = recoveryCodes();
      rows.set(email, { secret, backupCodes, enabled: false });
      return {
        totpURI: createOTP(secret, { digits: 6, period: 30 }).url(ISSUER, email),
        backupCodes: [...backupCodes],
      };
    },
    /** `verify-totp` with the session: turns the enrolment on. */
    async confirm(email: string, code: string): Promise<AuthFailure | null> {
      const row = rows.get(email);
      if (!row) return fail(400, "TWO_FACTOR_NOT_ENABLED");
      if (!(await createOTP(row.secret, { digits: 6, period: 30 }).verify(code.trim())))
        return fail(401, "INVALID_CODE");
      row.enabled = true;
      return null;
    },
    disable(email: string, password: string): AuthFailure | null {
      const bad = passwordFailure(email, password);
      if (bad) return bad;
      if (!rows.has(email)) return fail(400, "TWO_FACTOR_NOT_ENABLED");
      rows.delete(email);
      trusted.delete(email);
      return null;
    },
    regenerate(email: string, password: string): string[] | AuthFailure {
      const bad = passwordFailure(email, password);
      if (bad) return bad;
      const row = rows.get(email);
      if (!row?.enabled) return fail(400, "TWO_FACTOR_NOT_ENABLED");
      row.backupCodes = recoveryCodes();
      return [...row.backupCodes];
    },
  };
}

export type MfaFake = ReturnType<typeof createMfaFake>;
